import { apiService } from '../ApiService';
import { 
  InspectionGroup,
  InspectionLineItem,
    InspectionId,
  Inspection,
    TimeSensitivityStatus,
    InspectionResultStatus
} from '@/types/model/inspection';
import { InspectionResultGroupResponse, InspectionResponse, InspectionResultResponse } from '@/types/response/InspectionResultResponses';
import { GetLineItemsResponse } from '@/types/response/InspectionPlanningResponses';
import { InspectionScheduleLineItem } from '@/types/response/InspectionScheduleResponses';
import { DashboardToDoInspectionResponse } from '@/types/response/TrendAnalysisResponses';

import { lookupDataService } from '../data/LookupDataService';

import _ from 'lodash';


export class InspectionOrchestrationService {

    private readonly DEFAULT_TIME_SENSITIVITY_DAYS = 7;

    public async getGroupedInspectionData(groupIds: number[] = [], siteIds: number[] = [], areaIds: number[] = [], from?: string, to?: string): Promise<InspectionGroup[]> {
        try {
            const groupedInspectionResults = await apiService.getContractInspectionResults(from, to);
            const filteredGroupedInspectionResults = this.filterGroupedInspectionResults(groupedInspectionResults, groupIds, siteIds, areaIds);
            // console.log('Filtered grouped inspection results count:', filteredGroupedInspectionResults.length);
            const dashboardGroupIds = groupIds.length > 0
                ? groupIds
                : filteredGroupedInspectionResults.map(group => group.Id);

            const [scheduleLineItems, dashboardToDo, contractLineItemGroups] = await Promise.all([
                // apiService.getInspectionScheduleLineItems(fromDate, toDate)
                apiService.getAllInspectionScheduleLineItems()
                ,apiService.getContractDashboardToDo(dashboardGroupIds)
                ,apiService.getContractLineItems()
            ]);

            const filteredDashboardInspections = this.filterDashboardInspections(
                [...dashboardToDo.OutstandingInspections, ...dashboardToDo.UpcomingInspections],
                siteIds,
                areaIds
            );

            const mergedGroupedInspectionResults = this.mergeDashboardInspectionsIntoGroupedResults(
                filteredGroupedInspectionResults,
                filteredDashboardInspections,
                contractLineItemGroups
            );

            const filteredInspectionIds = _.chain(mergedGroupedInspectionResults)
                .flatMap(group => group.LineItems)
                .flatMap(lineItem => lineItem.InspectionResults)
                .map(result => result.Id)
                .uniq()
                .value();

            const detailedInspections = await apiService.getContractInspectionResultsForReport(filteredInspectionIds);

            const inspectionResultDetailMap = new Map<number, InspectionResponse>();
            detailedInspections.forEach(item => {
                item.InspectionResults.forEach(result => {
                    inspectionResultDetailMap.set(result.Id, result);
                });
            });

            const scheduledInspectionsMap = new Map<number, InspectionScheduleLineItem>();
            scheduleLineItems.forEach(item => {
                    scheduledInspectionsMap.set(item.Id, item);
            });

            const groups = mergedGroupedInspectionResults
                .map(group => this.buildInspectionGroup(group, inspectionResultDetailMap, scheduledInspectionsMap))
                .filter((group): group is InspectionGroup => group !== null);

            // Sort groups by groupIdentifier
            groups.sort((a, b) => a.groupIdentifier - b.groupIdentifier);

            return groups;
        } catch (error) {
            console.error('Error fetching grouped inspection data:', error);
            throw error;
        }
    }

    private buildInspectionGroup(
        group: InspectionResultGroupResponse,
        inspectionResultsMap: Map<number, InspectionResponse>,
        scheduledInspectionsMap: Map<number, InspectionScheduleLineItem>
    ): InspectionGroup | null {
        const lineItems = group.LineItems
            .map(lineItem => this.buildLineItem(group.Id, lineItem, inspectionResultsMap, scheduledInspectionsMap))
            .filter((lineItem): lineItem is InspectionLineItem => lineItem !== null);

        if (lineItems.length === 0) {
            return null;
        }

        return {
            id: group.Id,
            groupIdentifier: group.Identifier,
            title: group.Title,
            lineItems
        };
    }

    private filterGroupedInspectionResults(
        groupedInspectionResults: InspectionResultGroupResponse[],
        groupIds: number[],
        siteIds: number[],
        areaIds: number[]
    ): InspectionResultGroupResponse[] {
        const allowedGroupIds = groupIds.length > 0 ? new Set(groupIds) : null;
        const allowedSiteIds = siteIds.length > 0 ? new Set(siteIds) : null;
        const allowedAreaIds = areaIds.length > 0 ? new Set(areaIds) : null;

        return groupedInspectionResults
            .filter(group => !allowedGroupIds || allowedGroupIds.has(group.Id))
            .map(group => ({
                ...group,
                LineItems: group.LineItems
                    .filter(lineItem => !allowedAreaIds || allowedAreaIds.has(lineItem.ExpertiseAreaId))
                    .map(lineItem => ({
                        ...lineItem,
                        InspectionResults: lineItem.InspectionResults.filter(result => {
                            if (!allowedSiteIds) {
                                return true;
                            }

                            return typeof result.SiteId === 'number' && result.SiteId !== 0 && allowedSiteIds.has(result.SiteId);
                        })
                    }))
                    .filter(lineItem => lineItem.InspectionResults.length > 0)
            }))
            .filter(group => group.LineItems.length > 0);
    }

    private filterDashboardInspections(
        dashboardInspections: DashboardToDoInspectionResponse[],
        siteIds: number[],
        areaIds: number[]
    ): DashboardToDoInspectionResponse[] {
        const allowedSiteIds = siteIds.length > 0 ? new Set(siteIds) : null;
        const allowedAreaNames = areaIds.length > 0
            ? new Set(areaIds.map(areaId => lookupDataService.getExpertiseAreaNameById(areaId)))
            : null;

        return dashboardInspections.filter(inspection => {
            if (allowedSiteIds) {
                if (typeof inspection.SiteId !== 'number' || inspection.SiteId === 0 || !allowedSiteIds.has(inspection.SiteId)) {
                    return false;
                }
            }

            if (allowedAreaNames) {
                const areaName = inspection.AreaOfExpertise?.trim();
                if (!areaName || !allowedAreaNames.has(areaName)) {
                    return false;
                }
            }

            return true;
        });
    }

    private buildLineItem(
        groupId: number,
        lineItem: { Id: number; LineItem: string; Title: string; InspectionResults: InspectionResultResponse[] },
        inspectionResultsMap: Map<number, InspectionResponse>,
        scheduledInspectionsMap: Map<number, InspectionScheduleLineItem>,
    ): InspectionLineItem | null {

        const subItems: InspectionId[] = lineItem.InspectionResults
            .map(result => {
                const inspectionDetail = inspectionResultsMap.get(result.Id);
                const scheduledInspection = scheduledInspectionsMap.get(result.Id);
                const scheduledDate = result.ScheduledDate
                    ?? scheduledInspection?.NextInspectionDate
                    ?? null;
                const inspectedOn = result.InspectedOn
                    ?? scheduledInspection?.LastInspectionDate
                    ?? null;
                const status = this.deriveInspectionStatus(inspectionDetail, result);

                return {
                    id: result.Id,
                    status,
                    timeSensitivity: this.deriveTimeSensitivity(status, scheduledDate),
                    scheduledDate,
                    inspectedOn
                };
            });

        if (subItems.length === 0) {
            return null;
        }

        return {
            id: lineItem.Id,
            lineItem: lineItem.LineItem,
            title: lineItem.Title,
            groupId,
            subItems,
            inspections: null
        };
    }

    // private buildDashboardScheduledDatesMap(inspections: DashboardToDoInspectionResponse[]): Map<number, string> {
    //     const scheduledDatesMap = new Map<number, string>();

    //     inspections.forEach((inspection) => {
    //         if (inspection.ScheduledDate) {
    //             scheduledDatesMap.set(inspection.Id, inspection.ScheduledDate);
    //         }
    //     });

    //     return scheduledDatesMap;
    // }

    private mergeDashboardInspectionsIntoGroupedResults(
        groupedInspectionResults: InspectionResultGroupResponse[],
        dashboardInspections: DashboardToDoInspectionResponse[],
        contractLineItemGroups: GetLineItemsResponse
    ): InspectionResultGroupResponse[] {
        const mergedGroups: InspectionResultGroupResponse[] = groupedInspectionResults.map(group => ({
            ...group,
            LineItems: group.LineItems.map(lineItem => ({
                ...lineItem,
                InspectionResults: [...lineItem.InspectionResults]
            }))
        }));

        const groupById = new Map<number, InspectionResultGroupResponse>();
        const lineItemById = new Map<number, { groupId: number; lineItem: InspectionResultGroupResponse['LineItems'][number] }>();

        mergedGroups.forEach(group => {
            groupById.set(group.Id, group);
            group.LineItems.forEach(lineItem => {
                lineItemById.set(lineItem.Id, { groupId: group.Id, lineItem });
            });
        });

        const planningGroupByLineItemId = new Map<number, {
            groupId: number;
            groupIdentifier: number;
            groupTitle: string;
            lineItem: string;
            lineItemTitle: string;
            expertiseAreaId: number;
        }>();

        contractLineItemGroups.forEach(group => {
            const parsedIdentifier = Number.parseInt(group.Identifier, 10);
            const groupIdentifier = Number.isFinite(parsedIdentifier) ? parsedIdentifier : group.Id;

            group.LineItems.forEach(lineItem => {
                planningGroupByLineItemId.set(lineItem.Id, {
                    groupId: group.Id,
                    groupIdentifier,
                    groupTitle: group.Title,
                    lineItem: lineItem.LineItem,
                    lineItemTitle: lineItem.Title,
                    expertiseAreaId: lineItem.ExpertiseAreaId ?? 0
                });
            });
        });

        dashboardInspections.forEach(inspection => {
            const syntheticResult = this.createSyntheticInspectionResult(inspection);
            const existingLineItem = lineItemById.get(inspection.LineItemId);

            if (existingLineItem) {
                const hasInspection = existingLineItem.lineItem.InspectionResults.some(result => result.Id === syntheticResult.Id);
                if (!hasInspection) {
                    existingLineItem.lineItem.InspectionResults.push(syntheticResult);
                }
                return;
            }

            const planningMapping = planningGroupByLineItemId.get(inspection.LineItemId);
            if (!planningMapping) {
                console.warn('Unable to map dashboard inspection to line item/group:', inspection.Id, inspection.LineItemId);
                return;
            }

            let mappedGroup = groupById.get(planningMapping.groupId);
            if (!mappedGroup) {
                mappedGroup = {
                    Id: planningMapping.groupId,
                    Identifier: planningMapping.groupIdentifier,
                    Title: planningMapping.groupTitle,
                    LineItems: []
                };
                mergedGroups.push(mappedGroup);
                groupById.set(mappedGroup.Id, mappedGroup);
            }

            const mappedLineItem = {
                Id: inspection.LineItemId,
                LineItem: planningMapping.lineItem || inspection.LineItem,
                Title: planningMapping.lineItemTitle || inspection.Title,
                ExpertiseAreaId: planningMapping.expertiseAreaId,
                InspectionResults: [syntheticResult]
            };

            mappedGroup.LineItems.push(mappedLineItem);
            lineItemById.set(mappedLineItem.Id, { groupId: mappedGroup.Id, lineItem: mappedLineItem });
        });

        return mergedGroups;
    }

    private createSyntheticInspectionResult(inspection: DashboardToDoInspectionResponse): InspectionResultResponse {
        return {
            Id: inspection.Id,
            InspectedOn: inspection.LastInspected,
            SiteId: inspection.SiteId ?? 0,
            UserId: 0,
            ScheduledDate: inspection.ScheduledDate
        };
    }

    // private isWithinDateRange(dateString: string, from: Date, to: Date): boolean {
    //     console.warn('Checking date range for:', dateString, 'From:', from.toISOString(), 'To:', to.toISOString());
    //     const date = new Date(dateString);
    //     if (Number.isNaN(date.getTime())) {
    //         console.warn('Invalid date encountered:', dateString);
    //         return false;
    //     }

    //     console.warn('Parsed date:', date.toISOString(), 'Is within range:', date >= from && date <= to);
    //     return date >= from && date <= to;
    // }
    

    // private isAfterDate(dateString: string, from: Date): boolean {
    //     const date = new Date(dateString);
    //     if (Number.isNaN(date.getTime())) {
    //         return false;
    //     }
    //     return date >= from;
    // }

    private deriveInspectionStatus(
        inspectionDetail: InspectionResponse | undefined,
        inspectionResult: InspectionResultResponse
    ): InspectionResultStatus {

        if(!inspectionResult.InspectedOn && inspectionResult.ScheduledDate) {
            return 'not-started';
        }

        if(inspectionResult.InspectedOn && inspectionDetail) {
            if (inspectionDetail.IsInProgress) {
                return 'in-progress';
            } else if (inspectionDetail.ApprovalStatus === 'Approved') {
                return 'approved';
            } else if (inspectionDetail.ApprovalStatus === 'Disapproved') {
                return 'disapproved';
            } else if (!inspectionDetail.ApprovalStatus || inspectionDetail.ApprovalStatus === 'None') {
                return 'completed';
            }
        }

        return 'unknown';
    }

    private deriveTimeSensitivity(status: InspectionResultStatus, scheduledDate: string | null): TimeSensitivityStatus {
        return this.getTimeSensitivity(scheduledDate, status);
    }

    public async getInspections(ids: number[]): Promise<Inspection[]> {
        const inspections: Inspection[] = [];

        try {
            const [inspectionLineItems] = await Promise.all([
                apiService.getContractInspectionResultsForReport(ids)
            ]);

            inspectionLineItems.forEach(item => {
                item.InspectionResults.forEach(result => {
                    inspections.push({
                        inspectionResultId: result.Id,
                        inspectedOn: result.InspectedOn,
                        lineItemId: item.Id,
                        lineItem: item.LineItem,
                        title: item.Title,
                        siteName: lookupDataService.getSiteNameById(result.SiteId),
                        siteId: result.SiteId,
                        areaOfExpertise: lookupDataService.getExpertiseAreaNameById(item.ExpertiseAreaId),
                        areaOfExpertiseId: item.ExpertiseAreaId,
                        inspectorName: lookupDataService.getInspectorNameById(result.UserId ?? 0),
                        userId: result.UserId? result.UserId : 0,
                        compositeScore: result.CompositeScore,
                        isExceptional: result.IsExceptional ?? false,
                        timeSensitivity: 'unknown',
                        status: 'unknown',
                        checklistItems: result.ChecklistItems,
                        correctiveActions: null,
                        inspectionPlanBackground: {
                            LineItemPrimaryWeight: item.PrimaryValue ?? null,
                            LineItemAlternateWeight: item.AlternateValue ?? null,
                            TimeToInspect: item.TimeToInspect ?? null,
                            MethodOfInspection: lookupDataService.getInspectionMethodNameById(item.InspectionMethodId),
                            SampleSize: item.SampleSize ?? null,
                            SampleSelection: item.SampleSelection ?? null,
                            AdditionalInstructions: item.AdditionalInstructions ?? null,
                            InspectionPlanAttachments: item.InspectionPlanAttachments ?? []
                        },
                        lineItemDetail: {
                            PerformanceStandard: item.PerformanceStandard ?? null,
                            MajorRequirement: item.MajorRequirement ?? null,
                            RelatedInfo: item.RelatedInfo ?? null,
                            EstimatedQuantity: item.EstimatedQuantity ?? null,
                            LineItemAttachments: item.LineItemAttachments ?? []
                        }
                    });
                });
            });

            // Sort by Line Item, then Site name, then InspectedOn date (most recent first)
            inspections.sort((a, b) => {
            // First sort by Line Item
                const lineItemCompare = a.lineItem.localeCompare(b.lineItem);
                if (lineItemCompare !== 0) return lineItemCompare;

                // Then sort by Site name
                const siteCompare = a.siteName.localeCompare(b.siteName);
                if (siteCompare !== 0) return siteCompare;

                // Finally sort by InspectedOn date (most recent first)
                const dateA = new Date(a.inspectedOn || 0);
                const dateB = new Date(b.inspectedOn || 0);
                return dateB.getTime() - dateA.getTime();
            });
        } catch (error) {
            console.error('Error fetching inspections or corrective actions:', error);
            return [];
        }

        return inspections;
    }

    private getTimeSensitivityDaysThreshold(): number {
        const configuredDays = lookupDataService.getContractDetails()?.NidfPastDueDays;
        return typeof configuredDays === 'number' && configuredDays > 0
            ? configuredDays
            : this.DEFAULT_TIME_SENSITIVITY_DAYS;
    }

    private toStartOfDay(date: Date): Date {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    }

    private getDayDifferenceFromToday(dateString: string): number {
        const target = this.toStartOfDay(new Date(dateString));
        const today = this.toStartOfDay(new Date());
        const msPerDay = 24 * 60 * 60 * 1000;
        return Math.floor((target.getTime() - today.getTime()) / msPerDay);
    }

    private getTimeSensitivity(scheduledDate: string | null, status: InspectionResultStatus): TimeSensitivityStatus {
        if (!scheduledDate) {
            return 'unscheduled';
        }

        const thresholdDays = this.getTimeSensitivityDaysThreshold();
        const dayDifference = this.getDayDifferenceFromToday(scheduledDate);

        const isOpenStatus = status === 'not-started' || status === 'in-progress';
        const isDisapprovedStatus = status === 'disapproved';

        // Past Due: date is older than X days and status is open or disapproved.
        if (dayDifference < -thresholdDays && (isOpenStatus || isDisapprovedStatus)) {
            return 'past-due';
        }

        // Currently Due: date is within X days (past or future) and status is open or disapproved.
        if (Math.abs(dayDifference) <= thresholdDays && (isOpenStatus || isDisapprovedStatus)) {
            return 'due';
        }

        // Upcoming: date is more than X days in the future and status is open.
        if (dayDifference > thresholdDays && isOpenStatus) {
            return 'upcoming';
        }

        // Otherwise blank in UI (mapped from unscheduled in helpers).
        return 'unscheduled';
    }
}

export const inspectionOrchestrationService = new InspectionOrchestrationService();
export default inspectionOrchestrationService;
